/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useFocusOut,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { __ } from '@web-stories-wp/i18n';
import styled from 'styled-components';
import { Text, THEME_CONSTANTS } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import {
  createOptionFilter,
  isKeywordFilterable,
  getOptions,
  addUniqueEntries,
  getInset,
} from '../utils';
import { List, Group, GroupLabel, NoResult } from './styled';
import DefaultRenderer from './defaultRenderer';

const StyledLabel = styled(Text).attrs({
  forwardedAs: 'span',
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL,
})`
  color: ${({ theme }) => theme.colors.fg.tertiary};
`;

function OptionList({
  keyword = '',
  value = '',
  onSelect = () => {},
  onClose = () => {},
  onExpandedChange = () => {},
  focusTrigger = 0,
  options = [],
  primaryOptions,
  primaryLabel,
  priorityOptions = [],
  priorityOptionGroups = [],
  priorityLabel,
  searchResultsLabel,
  renderer = DefaultRenderer,
  onObserve,
  listId,
}) {
  const OptionRenderer = renderer;
  const listRef = useRef(null);
  const optionsRef = useRef([]);
  const [focusIndex, setFocusIndex] = useState(-1);
  const userSeenOptions = useRef([]);

  /*
   * KEYWORD FILTERING
   */
  const filteredListGroups = useMemo(() => {
    // If we're searching, return a single group of search results
    // gotten from all available options
    if (isKeywordFilterable(keyword) && options) {
      return [
        {
          label: searchResultsLabel,
          options: createOptionFilter(options)(keyword),
        },
      ];
    }
    // Otherwise return primary options in one group possibly preceded
    // by an optional list of priority options if such exist.
    return [
      ...(priorityOptions?.length
        ? [
            {
              label: priorityLabel,
              options: priorityOptions,
            },
          ]
        : []),
      ...(priorityOptionGroups?.length ? priorityOptionGroups : []),
      {
        label: primaryLabel,
        options: primaryOptions,
      },
    ];
  }, [
    keyword,
    options,
    priorityOptions,
    priorityOptionGroups,
    primaryOptions,
    priorityLabel,
    primaryLabel,
    searchResultsLabel,
  ]);

  /*
   * LAZY OPTIONS LOADING
   */
  // Add option when observed entry is seen
  const observer = useMemo(
    () =>
      new window.IntersectionObserver(
        (entries) => {
          if (onObserve) {
            const newlySeenOptions = entries
              .filter((entry) => entry.isIntersecting)
              .map((entry) => entry.target.dataset.option);
            userSeenOptions.current = addUniqueEntries(
              userSeenOptions.current,
              ...newlySeenOptions
            );
            onObserve(userSeenOptions.current);
          }
        },
        {
          root: listRef.current,
          // Gets ~2 extra items below scroll container BoundingBox
          rootMargin: '60px',
        }
      ),
    [onObserve]
  );

  // Observe rendered font options
  useLayoutEffect(() => {
    const renderedOptions = optionsRef.current;
    if (onObserve) {
      renderedOptions.forEach((option) => option && observer.observe(option));
    }
    return () => {
      if (onObserve) {
        renderedOptions.forEach(
          (option) => option && observer.unobserve(option)
        );
      }
      // clear existing option references before next update to filteredGroup
      optionsRef.current = [];
    };
  }, [observer, onObserve, filteredListGroups]);

  /*
   * KEYBOARD ACCESSIBILITY
   */
  const filteredOptions = useMemo(
    () => getOptions(filteredListGroups),
    [filteredListGroups]
  );

  const handleKeyPress = useCallback(
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();

      const { key } = evt;

      if (key === 'Escape') {
        onClose();
      } else if (key === 'Enter') {
        if (filteredOptions[focusIndex]) {
          onSelect(filteredOptions[focusIndex]);
        }
      } else if (key === 'ArrowUp') {
        setFocusIndex((index) => Math.max(0, index - 1));
      } else if (key === 'ArrowDown') {
        setFocusIndex((index) =>
          Math.min(filteredOptions.length - 1, index + 1)
        );
      }
    },
    [focusIndex, filteredOptions, onClose, onSelect]
  );

  useFocusOut(listRef, () => setFocusIndex(-1), []);

  // Scroll the selected or keyboard-focused option into view.
  // Only keyboard navigation moves focus away from search.
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) {
      return;
    }

    const scrollIndex =
      focusIndex === -1
        ? filteredOptions.findIndex((option) => option.id === value)
        : focusIndex;
    if (scrollIndex === -1) {
      listEl.scrollTo(0, 0);
      return;
    }

    const highlightedOptionEl = optionsRef.current[scrollIndex];
    if (!highlightedOptionEl) {
      return;
    }

    if (focusIndex !== -1) {
      highlightedOptionEl.focus();
    }
    listEl.scrollTo(0, highlightedOptionEl.offsetTop - listEl.clientHeight / 2);
  }, [focusIndex, filteredOptions, keyword, value]);

  /*
   * ACCESSIBILITY COMMUNICATION WITH SEARCH INPUT
   */
  const isExpanded = filteredOptions.length > 0;
  useEffect(() => onExpandedChange(isExpanded), [onExpandedChange, isExpanded]);
  useEffect(() => {
    if (focusTrigger > 0) {
      setFocusIndex(0);
    }
  }, [focusTrigger]);

  return filteredOptions.length <= 0 ? (
    <NoResult>{__('No matches found', 'web-stories')}</NoResult>
  ) : (
    <List
      ref={listRef}
      tabIndex={0}
      id={listId}
      role="listbox"
      onKeyDown={handleKeyPress}
      aria-label={__('Option List Selector', 'web-stories')}
      aria-required={false}
    >
      {filteredListGroups.map((group, i) => {
        const groupLabelId = `group-${uuidv4()}`;
        return (
          group.options.length > 0 && (
            <Group
              key={groupLabelId}
              role="group"
              aria-labelledby={groupLabelId}
            >
              {group.label && (
                <GroupLabel id={groupLabelId} role="presentation">
                  <StyledLabel>{group.label}</StyledLabel>
                </GroupLabel>
              )}
              {group.options.map((option, j) => {
                return (
                  <OptionRenderer
                    key={option.id}
                    role={'option'}
                    tabIndex="-1"
                    aria-selected={value === option.id}
                    aria-posinset={getInset(filteredListGroups, i, j)}
                    aria-setsize={filteredOptions.length}
                    data-option={option.id}
                    onClick={() => onSelect(option)}
                    ref={(el) =>
                      (optionsRef.current[getInset(filteredListGroups, i, j)] =
                        el)
                    }
                    option={option}
                    value={value}
                  />
                );
              })}
            </Group>
          )
        );
      })}
    </List>
  );
}

OptionList.propTypes = {
  keyword: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  onExpandedChange: PropTypes.func,
  focusTrigger: PropTypes.number,
  options: PropTypes.array,
  primaryOptions: PropTypes.array.isRequired,
  primaryLabel: PropTypes.string,
  priorityOptions: PropTypes.array,
  priorityLabel: PropTypes.string,
  searchResultsLabel: PropTypes.string,
  renderer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onObserve: PropTypes.func,
  listId: PropTypes.string.isRequired,
};

export default OptionList;
